import * as z from "@/lib/propSchema";

/**
 * Audit columns (`created_by` / `updated_by`) are NULLABLE in MySQL — rows written by a
 * migration or a system job have no user id, so the API sends `null`, not `undefined`.
 * Zod's `.optional()` only admits `undefined`, so a bare `z.number().optional()` REJECTS
 * those rows. That matters here because `index.tsx` returns `null` from the whole component
 * when `safeParse` fails: one null audit column makes the entire Program Structure section
 * vanish from the page. Every audit field is `.optional().nullable()` for that reason.
 */

export const ElectiveSchema = z.object({
    name: z.string().optional().default(""),
    priority: z.number().optional(),
    text_color: z.string().optional(),
});

export const ToolSchema = z.object({
    image: z.string().optional().default(""),
    priority: z.number().optional(),
    alt_image: z.string().optional().default(""),
    background_color: z.string().optional(),
});

export const SkillSchema = z.object({
    icon: z.string().optional().default(""),
    name: z.string().optional().default(""),
    alt_icon: z.string().optional().default(""),
    priority: z.number().optional(),
    text_color: z.string().optional(),
    background_color: z.string().optional(),
});

export const CategoryItemSchema = z.object({
    id: z.union([z.number(), z.string()]).optional(),
    category_id: z.number().optional(),
    name: z.string().optional().default(""),
    text_color: z.string().optional(),
    priority: z.number().optional(),
    is_active: z.number().optional(),
    status: z.coerce.string().optional(),
    is_deleted: z.number().optional(),
    created_by: z.number().optional().nullable(),
    updated_by: z.number().optional().nullable(),
    createdAt: z.string().optional().default(""),
    updatedAt: z.string().optional().default(""),
});

export const CategorySchema = z.object({
    id: z.union([z.number(), z.string()]).optional(),
    semester_id: z.number().optional(),
    name: z.string().optional().default(""),
    text_color: z.string().optional(),
    select_color: z.string().optional(),
    order_index: z.number().optional(),
    is_active: z.number().optional(),
    status: z.coerce.string().optional(),
    is_deleted: z.number().optional(),
    created_by: z.number().optional().nullable(),
    updated_by: z.number().optional().nullable(),
    createdAt: z.string().optional().default(""),
    updatedAt: z.string().optional().default(""),
    items: z.array(CategoryItemSchema).optional().default([]),
});

export const SemesterSchema = z.object({
    id: z.union([z.number(), z.string()]).optional(),
    program_fee_v2_id: z.number().optional(),
    name: z.string().optional().default(""),
    text_color: z.string().optional(),
    active_color: z.string().optional(),
    active_text_color: z.string().optional(),
    order_index: z.number().optional(),
    is_active: z.number().optional(),
    status: z.coerce.string().optional(),
    is_deleted: z.number().optional(),
    created_by: z.number().optional().nullable(),
    updated_by: z.number().optional().nullable(),
    createdAt: z.string().optional().default(""),
    updatedAt: z.string().optional().default(""),
    categories: z.array(CategorySchema).optional().default([]),
});

export const SubjectSchema = z.object({
    id: z.union([z.number(), z.string()]).optional(),
    program_fee_v2_id: z.number().optional(),
    semester_id: z.number().optional(),
    category_id: z.number().optional().nullable(),
    category_item_id: z.number().optional().nullable(),
    name: z.string().optional().default(""),
    type: z.string().optional().default(""),
    text_color: z.string().optional(),
    background_color: z.string().optional(),
    credits: z.number().optional().nullable(),
    is_elective: z.number().optional(),
    // elective_arrow_icon: z.string().optional().nullable().default(""),
    // elective_arrow_icon_alt: z.string().optional().nullable().default(""),
    electives: z.array(ElectiveSchema).optional().default([]),
    priority: z.number().optional(),
    is_active: z.number().optional(),
    status: z.coerce.string().optional(),
    is_deleted: z.number().optional(),
    created_by: z.number().optional().nullable(),
    updated_by: z.number().optional().nullable(),
    createdAt: z.string().optional().default(""),
    updatedAt: z.string().optional().default(""),
});

export const ProgramStructure2DataSchema = z.object({
    id: z.union([z.number(), z.string()]).optional(),
    page_id: z.number().optional(),
    component_id: z.number().optional(),
    // Layout/design variant: variant_1 = original dark theme, variant_2 = light Figma design.
    // Named `layout_variant` so it never collides with the section-variant id (which the form
    // writes to `variant`); the section `variant` is an unknown key here and is stripped by zod.
    layout_variant: z.enum(["variant_1", "variant_2"]).optional().default("variant_1"),
    title_1: z.string().optional().default(""),
    title_1_color: z.string().optional(),
    title_2: z.string().optional().default(""),
    title_2_gradient_start_color: z.string().optional(),
    title_2_gradient_end_color: z.string().optional(),
    description: z.string().optional().default(""),
    description_color: z.string().optional(),
    credits_text: z.string().optional().default(""),
    credits_text_color: z.string().optional(),
    total_credits: z.string().optional().default(""),
    total_credits_color: z.string().optional(),
    table_heading_1: z.string().optional().default(""),
    table_heading_1_color: z.string().optional(),
    table_heading_2: z.string().optional().default(""),
    table_heading_2_color: z.string().optional(),
    table_heading_3: z.string().optional().default(""),
    table_heading_3_color: z.string().optional(),
    table_header_background_color: z.string().optional(),
    note: z.string().optional().default(""),
    note_color: z.string().optional(),
    cta_button_text: z.string().optional().default(""),
    cta_button_text_color: z.string().optional(),
    cta_button_background_color: z.string().optional(),
    cta_button_icon: z.string().optional().default(""),
    cta_button_icon_alt: z.string().optional().default(""),
    cta_button_shadow_enabled: z.union([z.boolean(), z.number()]).optional().default(true),
    cta_button_shadow_color: z.string().optional(),
    tools_title: z.string().optional().default(""),
    tools_title_color: z.string().optional(),
    skills_title: z.string().optional().default(""),
    skills_title_color: z.string().optional(),
    tools: z.array(ToolSchema).optional().default([]),
    skills: z.array(SkillSchema).optional().default([]),
    table_total_text: z.string().optional().default(""),
    table_total_text_color: z.string().optional(),
    semester_active_bg_color: z.string().optional(),
    semester_active_text_color: z.string().optional(),
    semester_inactive_border_color: z.string().optional(),
    semester_inactive_text_color: z.string().optional(),
    background_color: z.string().optional(),
    backvariant: z.enum(["variant_1", "variant_2", "variant_3"]).optional().default("variant_3"),
    gradient_color_1: z.string().optional(),
    gradient_color_2: z.string().optional(),
    gradient_color_3: z.string().optional(),
    gradient_color_4: z.string().optional(),
    enable_animation: z.union([z.boolean(), z.number()]).optional().default(false),
    status: z.coerce.string().optional(),
    is_deleted: z.number().optional(),
    created_by: z.number().optional().nullable(),
    updated_by: z.number().optional().nullable(),
    createdAt: z.string().optional().default(""),
    updatedAt: z.string().optional().default(""),
    semesters: z.array(SemesterSchema).optional().default([]),
    subjects: z.array(SubjectSchema).optional().default([]),
});

export type ProgramStructure2Data = z.infer<typeof ProgramStructure2DataSchema>;
